import { isFuture, isValid, parseISO } from 'date-fns';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isURL from 'validator/es/lib/isURL';

import { formatRole, toSentenceCase } from '../../utils';

import { COMMUNITY_IMAGES_LIMIT } from '../Community/constants';
import { SR_IMAGES_LIMIT } from '../ServiceRequest/constants';

export const validateEmail = (email) => {
  if (!email) {
    return null;
  }
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email) ? null : 'Not a valid e-mail';
};

export const validatePhoneNumber = (value) => {
  if (!value) return null;
  const re = /^[+]{0,1}\d+$/g;
  return re.test(value)
    ? null
    : `Phone number should be numeric with the optional '+' sign in the beginning`;
};

export const validatePassword = (value) => {
  if (!value) return null;
  const reAnySpace = /[\s\r\n\u0085\u2028\u2029]+/g;
  if (reAnySpace.test(value)) {
    return 'Passwords can’t include spaces';
  }
  const rePassword = /^(?=.*[A-Za-z])(?=.*[0-9])[A-Za-z\d!@#$%^&*]{8,}$/im;
  return rePassword.test(value)
    ? null
    : 'Passwords should have 8 characters or more with at least one letter and one digit and can contain allowed symbols !@#$%^&*';
};

export const required = (value) => {
  if (!value || isNil(value) || (typeof value === 'object' && isEmpty(value))) {
    return 'This field is required';
  }
  return undefined;
};

const KB = 1024;
export const MB = KB * KB;

export const getBytesInMegabytes = (value) => MB * value;

export const requiredAndMaxSizeLimit = (value) => {
  const maxSize = 10;
  if (!value || isNil(value) || isEmpty(value)) {
    return 'This field is required';
  }
  if (value[0].size > getBytesInMegabytes(maxSize)) {
    return `File cannot be larger than ${maxSize} MB.`;
  }
  return undefined;
};

export const notNullOrUndefined = (value) => {
  if (value === null || value === undefined) {
    return 'Value is required';
  }
  return undefined;
};

export const validateRadius = (value) => {
  if (!value) return undefined;
  if (Number.isNaN(Number(value)) || value <= 0 || value > 10000)
    return 'Ensure radius is in the range of 1 - 10000 meters';
  if (!Number.isInteger(Number(value))) return 'Ensure radius is an integer';
  return undefined;
};

export const validateUrl = (value) => {
  if (!value || isURL(String(value))) {
    return undefined;
  }
  return `Please, enter a valid URL`;
};

export const validateUrlOrTelOrSms = (value) => {
  const regex = /^(sms|tel):.{1,18}$/;
  if (!value || isURL(String(value)) || regex.test(value)) {
    return undefined;
  }
  return `Please, enter a valid URL`;
};

export const validateHttpsUrl = (value) => {
  if (
    !value ||
    isURL(String(value), { require_protocol: true, protocols: ['https'] })
  ) {
    return undefined;
  }
  return `For your security the 'https' protocol is required`;
};

export const validateAllowedHours = (value, values) => {
  const { allowedTimeStart, allowedTimeEnd } = values;
  if (allowedTimeStart && !allowedTimeEnd) {
    return 'End time is required';
  }
  if (!allowedTimeStart && allowedTimeEnd) {
    return 'Start time is required';
  }
  return undefined;
};

export const validateDate = (value) => {
  if (!value) return null;
  const date = parseISO(value);
  if (!isValid(date)) return 'Invalid date';
  return null;
};

export const validateFutureDate = (value) => {
  if (!value) return null;
  const date = parseISO(value);
  if (!isFuture(date)) return 'Invalid date';
  return null;
};

function IPtoNum(ip) {
  return Number(
    ip
      .split('.')
      .map((d) => `000${d}`.substr(-3))
      .join(''),
  );
}

export const validateIP = (value, values, props) => {
  if (value) {
    const re =
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (!re.test(value)) {
      return 'You have entered an invalid IP address';
    }

    const { start_ip, end_ip } = values;
    if (start_ip && end_ip && re.test(start_ip) && re.test(end_ip)) {
      const lastStartDot = start_ip.lastIndexOf('.');
      const lastEndDot = end_ip.lastIndexOf('.');
      if (start_ip.slice(0, lastStartDot) !== end_ip.slice(0, lastEndDot)) {
        return (
          value === end_ip && 'The allowed range is up to 256 IP addresses'
        );
      }
      const { existingIpRanges } = props;
      const existingRange = existingIpRanges.find(
        ({ startIp, endIp }) =>
          IPtoNum(startIp) <= IPtoNum(value) &&
          IPtoNum(endIp) >= IPtoNum(value),
      );
      if (existingRange) {
        const { startIp, endIp, name } = existingRange;
        return `Given IP range falls between the "${name}" range (${startIp} - ${endIp})`;
      }
    }
  }
  return undefined;
};

export const formatSelect = (options, labelKey, valueKey) => {
  if (!options?.length) return [];
  return options.map((option) => ({
    label: labelKey ? option[labelKey] : option.name,
    value: valueKey ? option[valueKey] : option.id,
  }));
};

export const formatFilterSelect = (
  filterOptions,
  options,
  labelKey,
  valueKey,
) => {
  const filteredOptions = filterOptions?.length
    ? options?.filter(({ id }) => !filterOptions?.includes(id))
    : options;
  return filteredOptions
    ? formatSelect(filteredOptions, labelKey, valueKey)
    : [];
};

export const formatSelectWithFixedValues = (options) => {
  if (!options?.length) return [];
  return options.map((option = {}) => {
    const { name, id, defaultInBuilding } = option;
    return {
      label: name,
      value: id,
      isFixed: defaultInBuilding,
    };
  });
};

export const formatDeliverySelect = (options) => {
  if (!options) return [];
  return options.map((option) => ({
    label: option.name,
    value: option.id,
    icon: option?.icons?.[0]?.url,
  }));
};

export const formatTextSelect = (options) => {
  if (!options) return [];
  return options.map((option) => ({
    label: toSentenceCase(option),
    value: option,
  }));
};

export const formatSelectPersonAndRole = (options, propertyIds, roleTypes) => {
  if (!options) return [];
  return options.map((option) => {
    const allRoles = Object.assign([], option.residentRoles, option.staffRoles);
    const roleId = get(
      allRoles.find((r) => propertyIds.indexOf(Number(r.propertyId) > -1)),
      'role',
    );
    const role = roleId
      ? roleTypes.find((rt) => Number(rt.id) === Number(roleId))
      : null;
    return {
      label: `${option.firstName} ${option.lastName} - ${formatRole(
        get(role, 'name', ''),
      )}`,
      value: option.id,
    };
  });
};

export const doorDelay = (value) => {
  if (value < 0 || value > 120) {
    return 'Must be between 0 and 120 seconds';
  }
  return undefined;
};

// TODO: consider removing since mui select will be used
export const checkFixedValue = (value) => value.filter((v) => v?.isFixed);

export const formatReactSelectValue = (value, multi) => {
  if (!value) return null;
  let formattedValue = null;
  if (multi) {
    formattedValue = value
      .map((item) => item?.value)
      .filter((item) => item !== null && item !== undefined);
  } else {
    formattedValue = value?.value ?? null;
  }
  return formattedValue;
};

export const formatFileSize = (number) => {
  if (number < KB) {
    return `${number} + bytes`;
  }
  if (number >= KB && number < MB) {
    return `${(number / KB).toFixed(1)} KB`;
  }
  if (number >= MB) {
    return `${(number / MB).toFixed(1)} MB`;
  }
  return undefined;
};

export const maxFileSize = (files) => {
  const limitMB = 10;
  if (!files) {
    return undefined;
  }
  let error = null;
  files?.forEach((file) => {
    const size = file?.file?.size ?? file?.size;
    if (size > getBytesInMegabytes(limitMB)) {
      error = `File cannot be larger than ${limitMB} MB`;
    }
  });
  return error;
};

const validateFileLimit = (files, maxCount) => {
  if (files?.length > maxCount) {
    return `Up to ${maxCount} images can be uploaded`;
  }
  return null;
};

export const communityImagesLimit = (files) =>
  validateFileLimit(files, COMMUNITY_IMAGES_LIMIT);

export const srImagesLimit = (files) =>
  validateFileLimit(files, SR_IMAGES_LIMIT);

export const onlyNumeric = (value) => {
  if (!value) return null;
  const re = /^[0-9]*$/;
  return re.test(value) ? null : 'Must be a positive integer';
};

export const facilityCodeRange = (value) =>
  value >= 0 && value <= 255
    ? null
    : `Can't be less than 0 and greater than 255`;

export const cardNumberRange = (value) =>
  value >= 0 && value <= 65535
    ? null
    : `Can't be less than 0 and greater than 65,535`;

export const allowedCardNumber = (value, values, props) => {
  const { batch } = props;
  const { accessCardBatch, number } = values;
  if (!accessCardBatch || !number) return undefined;
  const selectedBatch =
    accessCardBatch &&
    Array.isArray(batch) &&
    batch.find((item) => item.id === accessCardBatch);
  if (!selectedBatch) return undefined;
  const { firstCardNumber, lastCardNumber } = selectedBatch;
  return Number(firstCardNumber) <= number && +lastCardNumber >= number
    ? null
    : `The value should be in the range of ${firstCardNumber} - ${lastCardNumber}`;
};

export const lastCardNumberValidation = (value, values) => {
  if (values.lastCardNumber <= values.firstCardNumber) {
    return `Should be greater than the first card number value`;
  }
  return undefined;
};

export const validateReportDates = (value, values = {}) => {
  const { startDate, endDate } = values;
  if (value && startDate && endDate) {
    if (startDate > endDate && value === startDate)
      return 'the start date cannot be after the end date';
  }
  return undefined;
};

const lengthValidation = (value, limit) => {
  if (!value || !value?.length || !limit) return null;
  return value.length > limit
    ? `Ensure this field has no more than ${limit} characters`
    : null;
};

const lengthValidation50 = (value) => lengthValidation(value, 50);
const lengthValidation60 = (value) => lengthValidation(value, 60);
const lengthValidation64 = (value) => lengthValidation(value, 64);
const lengthValidation120 = (value) => lengthValidation(value, 120);
const lengthValidation200 = (value) => lengthValidation(value, 200);
const lengthValidation250 = (value) => lengthValidation(value, 250);
const lengthValidation255 = (value) => lengthValidation(value, 255);
const lengthValidation500 = (value) => lengthValidation(value, 500);
const lengthValidation1000 = (value) => lengthValidation(value, 1000);
const lengthValidation1500 = (value) => lengthValidation(value, 1500);
const lengthValidation2000 = (value) => lengthValidation(value, 2000);

lengthValidation50.limit = 50;
lengthValidation60.limit = 60;
lengthValidation64.limit = 64;
lengthValidation120.limit = 120;
lengthValidation200.limit = 200;
lengthValidation250.limit = 250;
lengthValidation255.limit = 255;
lengthValidation500.limit = 500;
lengthValidation1000.limit = 1000;
lengthValidation1500.limit = 1500;
lengthValidation2000.limit = 2000;

export {
  lengthValidation50,
  lengthValidation60,
  lengthValidation64,
  lengthValidation120,
  lengthValidation200,
  lengthValidation250,
  lengthValidation255,
  lengthValidation500,
  lengthValidation1000,
  lengthValidation1500,
  lengthValidation2000,
};

export const getCharacterLimit = (validators) => {
  if (validators?.length) {
    const lengthValidator = validators.find((validator) => validator.limit);
    if (lengthValidator?.limit) {
      return lengthValidator.limit;
    }
    return undefined;
  }
  return undefined;
};

export const floorValidation = (value) => {
  if (value) {
    return /^\d+$/.test(value?.replace(' ', ''))
      ? null
      : 'Must be a single number';
  }
  return undefined;
};
